import * as React from 'react'
import { Link } from 'react-router-dom'

import { clsx } from 'clsx'

import { Card, Flex, Text } from '@buffer-mono/popcorn'

import styles from './ExploreCards.module.css'

import type { JSX } from 'react'

type ExploreCardBody = {
  icon: React.ReactNode
  title: string
  description: string
}

const CardBody = ({
  icon,
  title,
  description,
}: ExploreCardBody): JSX.Element => (
  <Card className={styles.actionCard} padding="md">
    <Flex gap="sm" align="start">
      <span className={styles.actionIcon}>{icon}</span>
      <Flex direction="column" gap="2xs">
        <Text weight="medium">{title}</Text>
        <Text size="sm" color="subtle">
          {description}
        </Text>
      </Flex>
    </Flex>
  </Card>
)

type ExploreActionCardProps = React.ComponentPropsWithoutRef<'button'> &
  ExploreCardBody

/**
 * A suggestion card that runs an action. The whole card is the button, and it
 * forwards its props and ref, so a wrapper that injects behavior (the composer
 * trigger) can drive it without nesting a second control inside.
 */
export const ExploreActionCard = React.forwardRef<
  HTMLButtonElement,
  ExploreActionCardProps
>(function ExploreActionCard(
  { icon, title, description, className, ...props },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={clsx(styles.actionRoot, className)}
    >
      <CardBody icon={icon} title={title} description={description} />
    </button>
  )
})

type ExploreLinkCardProps = ExploreCardBody & {
  /** In-app route the card navigates to. */
  to: string
}

/**
 * A suggestion card that navigates. A real link rather than a button that
 * calls `navigate`, so it keeps middle-click and open-in-new-tab.
 */
export const ExploreLinkCard = ({
  to,
  icon,
  title,
  description,
}: ExploreLinkCardProps): JSX.Element => (
  <Link to={to} className={styles.actionRoot}>
    <CardBody icon={icon} title={title} description={description} />
  </Link>
)

type EmptyStateExploreCardsProps = {
  /** The suggestion cards to offer, in display order. */
  children: React.ReactNode
}

/**
 * The "keep exploring" suggestion column shown under an inbox empty state,
 * pointing the user at something useful while their inbox has nothing to act
 * on. Each inbox supplies its own cards, so the set can differ per tab.
 */
export function EmptyStateExploreCards({
  children,
}: EmptyStateExploreCardsProps): JSX.Element {
  return (
    <Flex
      direction="column"
      gap="sm"
      align="stretch"
      className={styles.container}
    >
      <Text size="sm" color="subtle" align="center">
        In the meantime, keep exploring:
      </Text>

      <Flex direction="column" gap="sm">
        {children}
      </Flex>
    </Flex>
  )
}
